import { useState } from "react";
import { Modal } from "../../../ui/molecules/Modal";
import { ModalActions } from "../../../ui/molecules/ModalActions";
import { useSubmitBugReport } from "../../../lib/api/feedback.api";
import { APP_VERSION } from "../../../shared/version";

const CATEGORIES = [
  { value: "ui", label: "Interface / Affichage" },
  { value: "performance", label: "Performance / Lenteur" },
  { value: "data", label: "Perte de données" },
  { value: "broken", label: "Fonctionnalité cassée" },
  { value: "other", label: "Autre" },
] as const;

function collectSystemInfo() {
  return {
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    appVersion: APP_VERSION,
    currentPage: window.location.pathname,
  };
}

interface BugReportModalProps {
  readonly onClose: () => void;
}

export function BugReportModal({ onClose }: BugReportModalProps) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submitMutation = useSubmitBugReport();

  const canSubmit = title.trim().length > 0 && category !== "" && description.trim().length >= 10;

  const handleSubmit = () => {
    if (!canSubmit || submitMutation.isPending) return;

    submitMutation.mutate(
      {
        title: title.trim(),
        category,
        description: description.trim(),
        systemInfo: collectSystemInfo(),
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  if (submitted) {
    return (
      <Modal title="Merci !" onClose={onClose} maxWidth="md">
        <div className="text-center py-6">
          <div className="text-4xl mb-4">✅</div>
          <p className="text-ink font-medium mb-2">Votre rapport a bien été envoyé</p>
          <p className="text-ink-muted text-sm">
            Nous allons examiner votre retour et travailler à l'amélioration de Palnia.
          </p>
          <ModalActions
            mode="custom"
            onCancel={onClose}
            customPrimaryAction={{
              label: "Fermer",
              onClick: onClose,
              variant: "primary",
            }}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Signaler un bug"
      description="Aidez-nous à améliorer Palnia en signalant un problème"
      onClose={onClose}
      maxWidth="lg"
      footer={
        <ModalActions
          mode="create"
          hasChanges={canSubmit}
          isSaving={submitMutation.isPending}
          isValid={canSubmit}
          onCancel={onClose}
          onSave={handleSubmit}
          saveLabel="Envoyer"
        />
      }
    >
      <div className="flex flex-col gap-5">
        {/* Titre */}
        <div>
          <label htmlFor="bug-title" className="block text-sm font-medium text-ink mb-1.5">
            Titre
          </label>
          <input
            id="bug-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Le calendrier ne s'affiche pas correctement"
            className="w-full px-3 py-2 bg-surface-alt border border-border rounded-lg text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-accent/30"
            maxLength={200}
          />
        </div>

        {/* Catégorie */}
        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">
            Catégorie
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors min-h-[44px] touch-feedback ${
                  category === cat.value
                    ? "bg-accent text-white border-accent"
                    : "bg-surface-alt border-border text-ink-muted hover:text-ink hover:border-ink-muted/30"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="bug-description" className="block text-sm font-medium text-ink mb-1.5">
            Description
          </label>
          <textarea
            id="bug-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Décrivez le problème en détail..."
            rows={5}
            className="w-full px-3 py-2 bg-surface-alt border border-border rounded-lg text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
            maxLength={5000}
          />
          <p className="text-xs text-ink-muted mt-1">
            {description.length < 10
              ? `Minimum 10 caractères (${description.length}/10)`
              : `${description.length}/5000`}
          </p>
        </div>

        {submitMutation.isError && (
          <p className="text-sm text-danger">
            Une erreur est survenue. Veuillez réessayer.
          </p>
        )}
      </div>
    </Modal>
  );
}
